import { getMomentTz, TimeService } from "@dexteel/mesf-core";
import { Event } from "../models/Event";
import { ColumnDef, ProcessedRow } from "../models/utils";
import {
	getEventMonthScheduledRatio,
	isMultiMonthEvent,
} from "./calculateFields";
import { resolveTargetTPH } from "./targetTPH";

const moment = getMomentTz();

const roundToSingleDecimal = (value: number | null): number | undefined =>
	value == null ? undefined : Math.round(value * 10) / 10;

//  Process raw events into rows for the table, including multi-day splitting and MTD/Month summary rows
export const processEventsToRows = (
	data: Event[] | undefined,
	month: number,
	year: number,
): ProcessedRow[] => {
	if (!data) return [];

	const serverTimeZone = TimeService.getInstance().getServerTimeZone();
	const daysInMonth = new Date(year, month + 1, 0).getDate();
	const result: ProcessedRow[] = [];
	const dayMap = new Map<string, ProcessedRow[]>();

	const sampleDate = moment.tz(
		`${year}-${String(month + 1).padStart(2, "0")}-15`,
		serverTimeZone,
	);
	const utcOffsetHours = -sampleDate.utcOffset() / 60;

	const monthStartDate = moment
		.tz(`${year}-${String(month + 1).padStart(2, "0")}-01`, serverTimeZone)
		.startOf("day");
	const monthEndDate = moment
		.tz(
			`${year}-${String(month + 1).padStart(2, "0")}-${daysInMonth}`,
			serverTimeZone,
		)
		.startOf("day");

	const today = moment.tz(serverTimeZone).startOf("day");
	const isCurrentMonth = today.year() === year && today.month() === month;

	const inProgressEvent = data.find(
		(e) => e.StatusCode === "I" && e.EventId > 0,
	);

	let thresholdDay = today;
	if (inProgressEvent) {
		const ipEnd = moment
			.tz(inProgressEvent.End ?? inProgressEvent.Start, serverTimeZone)
			.startOf("day");
		if (
			ipEnd.isSameOrAfter(monthStartDate) &&
			ipEnd.isSameOrBefore(monthEndDate)
		) {
			thresholdDay = moment.min(ipEnd, today);
		}
	}
	const thresholdKey = `${thresholdDay.year()}-${thresholdDay.month()}-${thresholdDay.date()}`;

	const fullDayNonProductionEvents = new Map<string, boolean>();

	const monthEndExclusiveMs = monthEndDate.clone().add(1, "day").valueOf();
	const monthStartMs = monthStartDate.valueOf();
	const nonProdIntervals: Array<[number, number]> = [];
	data.forEach((event) => {
		if (event.EventTypeCode === "P") return;
		const s = moment.tz(event.Start, serverTimeZone).valueOf();
		const e = event.End
			? moment.tz(event.End, serverTimeZone).valueOf()
			: moment.tz(serverTimeZone).valueOf();
		const clipS = Math.max(s, monthStartMs);
		const clipE = Math.min(e, monthEndExclusiveMs);
		if (clipE > clipS) nonProdIntervals.push([clipS, clipE]);
	});
	nonProdIntervals.sort((a, b) => a[0] - b[0]);
	const mergedNonProd: Array<[number, number]> = [];
	for (const [s, e] of nonProdIntervals) {
		const last = mergedNonProd[mergedNonProd.length - 1];
		if (last && last[1] >= s) {
			last[1] = Math.max(last[1], e);
		} else {
			mergedNonProd.push([s, e]);
		}
	}

	const subtractNonProdOverlap = (startMs: number, endMs: number): number => {
		let overlap = 0;
		for (const [s, e] of mergedNonProd) {
			if (e <= startMs) continue;
			if (s >= endMs) break;
			overlap += Math.min(endMs, e) - Math.max(startMs, s);
		}
		return overlap;
	};

	data.forEach((event) => {
		const eventStart = moment.tz(event.Start, serverTimeZone);
		const eventEnd = event.End
			? moment.tz(event.End, serverTimeZone)
			: moment.tz(serverTimeZone);

		let start = eventStart.clone();
		let end = eventEnd.clone();

		const eventStartDate = eventStart.clone().startOf("day");
		const eventEndDate = eventEnd.clone().startOf("day");

		if (eventStartDate.isBefore(monthStartDate)) {
			start = monthStartDate.clone().hour(utcOffsetHours);
		}

		if (eventEndDate.isAfter(monthEndDate)) {
			end = monthEndDate.clone().hour(utcOffsetHours).endOf("day");
		}

		const diffDays = end
			.clone()
			.startOf("day")
			.diff(start.clone().startOf("day"), "days");

		const isProduction = event.EventTypeCode === "P";
		const clipStartMs = Math.max(eventStart.valueOf(), monthStartMs);
		const clipEndMs = Math.min(eventEnd.valueOf(), monthEndExclusiveMs);
		const eventSpansOutsideMonth =
			clipStartMs > eventStart.valueOf() || clipEndMs < eventEnd.valueOf();

		let proratedScheduledHours: number | null = null;
		let proratedActualHours: number | null = null;

		if (isProduction) {
			proratedActualHours = event.ActualRollingHours;
			if (eventSpansOutsideMonth) {
				const ratio = getEventMonthScheduledRatio(
					eventStart.toDate(),
					eventEnd.toDate(),
					month,
					event.ScheduledRollingHours,
				);
				proratedScheduledHours =
					event.ScheduledRollingHours != null
						? event.ScheduledRollingHours * ratio
						: null;
				// ActualRollingHours is already month-specific (ProductionEventActuals JOIN by @QueryMonthStart).
				// Fall back to wall-clock estimate only when PEA has not yet been written (in-progress, current month).
				if (event.ActualRollingHours != null) {
					proratedActualHours = event.ActualRollingHours;
				} else {
					const overlapMs = subtractNonProdOverlap(clipStartMs, clipEndMs);
					proratedActualHours = Math.max(
						0,
						(clipEndMs - clipStartMs - overlapMs) / 3600000,
					);
				}
			} else {
				proratedScheduledHours = event.ScheduledRollingHours;
			}
		}

		if (diffDays >= 0) {
			for (let i = 0; i <= diffDays; i++) {
				const currentDate = start.clone().add(i, "days");
				const dayKey = `${currentDate.year()}-${currentDate.month()}-${currentDate.date()}`;

				if (event.EventTypeCode !== "P") {
					const dayStart = currentDate.clone().startOf("day");
					const dayEnd = currentDate.clone().endOf("day");

					const eventCoversFullDay =
						eventStart.isSameOrBefore(dayStart) &&
						eventEnd.isSameOrAfter(dayEnd);

					if (eventCoversFullDay) {
						fullDayNonProductionEvents.set(dayKey, true);
					}
				}

				if (i === 0) {
					const row = {
						...event,
						Start: start.toDate(),
						End: end.toDate(),
						OriginalStart: event.Start ?? undefined,
						OriginalEnd: event.End ?? undefined,
						ParentEventId: event.EventId,
						IsParent: true,
						IsFirstRow: true,
						RowIndex: 0,
						ScheduledHours: proratedScheduledHours,
						ActualHours: proratedActualHours,
					} as ProcessedRow;
					if (!dayMap.has(dayKey)) dayMap.set(dayKey, []);
					dayMap.get(dayKey)!.push(row);
				} else {
					const placeholder = {
						...event,
						IsParent: false,
						IsFirstRow: false,
						Start: currentDate.toDate(),
						End: currentDate.toDate(),
						OriginalStart: event.Start ?? undefined,
						OriginalEnd: event.End ?? undefined,
						RowIndex: i,
						ScheduledHours: proratedScheduledHours,
						ActualHours: proratedActualHours,
					} as ProcessedRow;
					if (!dayMap.has(dayKey)) dayMap.set(dayKey, []);
					dayMap.get(dayKey)!.push(placeholder);
				}
			}
		} else {
			const dayKey = `${start.year()}-${start.month()}-${start.date()}`;
			if (!dayMap.has(dayKey)) dayMap.set(dayKey, []);
			dayMap.get(dayKey)!.push({
				...event,
				IsFirstRow: true,
				ScheduledHours: proratedScheduledHours,
				ActualHours: proratedActualHours,
			} as ProcessedRow);
		}
	});

	for (const [dayKey, hasFullDayNonProd] of fullDayNonProductionEvents) {
		if (hasFullDayNonProd && dayMap.has(dayKey)) {
			const events = dayMap.get(dayKey)!;
			const filtered = events.filter((e) => e.EventTypeCode !== "P");
			dayMap.set(dayKey, filtered);
		}
	}

	// Build result with MTD summary
	const countedEventIdsMTD = new Set<number>();
	const countedEventIdsMonth = new Set<number>();

	let mtdChargedTons = 0;
	let mtdRolledTons = 0;
	let mtdFinishedTons = 0;
	let mtdRollingHours = 0;
	let mtdSchedRollingHours = 0;
	let mtdTargetWeightedTPH = 0;
	let mtdTargetWeightHours = 0;
	let mtdSchedCharged = 0;
	let mtdSchedRolled = 0;
	let mtdSchedFinished = 0;
	let mtdActCharged = 0;
	let mtdActRolled = 0;
	let mtdActFinished = 0;

	let monthSchedCharged = 0;
	let monthSchedRolled = 0;
	let monthSchedFinished = 0;
	let monthSchedHours = 0;
	let monthTargetWeightedTPH = 0;
	let monthTargetWeightHours = 0;
	let monthActCharged = 0;
	let monthActRolled = 0;
	let monthActFinished = 0;
	let monthActHours = 0;

	let foundThreshold = false;
	let thresholdInsertIndex = -1;

	for (let day = 1; day <= daysInMonth; day++) {
		const currentDate = moment.tz(
			`${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(
				2,
				"0",
			)} 04:00:00`,
			serverTimeZone,
		);
		const dayKey = `${currentDate.year()}-${currentDate.month()}-${currentDate.date()}`;

		const isThresholdDay = dayKey === thresholdKey;

		if (isThresholdDay) {
			foundThreshold = true;
		}

		if (dayMap.has(dayKey)) {
			const events = dayMap.get(dayKey)!;
			events.forEach((event) => {
				if (event.EventTypeCode === "P" || event.IsFirstRow !== false) {
					result.push(event);
					return;
				}

				const previousRowIndex = result.findLastIndex(
					(row) => row.EventId === event.EventId,
				);

				if (previousRowIndex === -1) {
					result.push(event);
					return;
				}

				result.splice(previousRowIndex + 1, 0, event);
			});

			events.forEach((event) => {
				if (!event.EventId) return;

				const statusCode = event.StatusCode;
				const useActual = statusCode === "C" || statusCode === "I";

				// Prorate multi-month events to the displayed month so the MTD and
				// Month summaries match the per-row Scheduled column.
				const origStart = event.OriginalStart ?? event.Start;
				const origEnd = event.OriginalEnd ?? event.End;
				const multi = isMultiMonthEvent(origStart, origEnd);
				const ratio = multi
					? getEventMonthScheduledRatio(
							origStart,
							origEnd,
							month,
							event.ScheduledRollingHours,
						)
					: 1;
				const scheduledHours = (event.ScheduledRollingHours || 0) * ratio;
				const effectiveTargetTPH =
					event.EffectiveTargetTPH ??
					resolveTargetTPH({
						TargetTPH: event.TargetTPH,
						SectionTargetTPH: event.SectionTargetTPH,
						TargetTPHPercentage: event.TargetTPHPercentage,
						SectionTargetTPHPercentage: event.SectionTargetTPHPercentage,
					});

				if (
					(!foundThreshold || isThresholdDay) &&
					!countedEventIdsMTD.has(event.EventId) &&
					useActual
				) {
					countedEventIdsMTD.add(event.EventId);

					mtdSchedCharged += (event.ScheduledChargedTons || 0) * ratio;
					mtdSchedRolled += (event.ScheduledRolledTons || 0) * ratio;
					mtdSchedFinished += (event.ScheduledFinishedTons || 0) * ratio;
					mtdSchedRollingHours += scheduledHours;
					if (effectiveTargetTPH != null && scheduledHours > 0) {
						mtdTargetWeightedTPH += effectiveTargetTPH * scheduledHours;
						mtdTargetWeightHours += scheduledHours;
					}
					if (useActual) {
						mtdActCharged += event.ActualChargedTons || 0;
						mtdActRolled += event.ActualRolledTons || 0;
						mtdActFinished += event.ActualFinishedTons || 0;

						mtdChargedTons += event.ActualChargedTons || 0;
						mtdRolledTons += event.ActualRolledTons || 0;
						mtdFinishedTons += event.ActualFinishedTons || 0;
						mtdRollingHours += event.ActualRollingHours || 0;
					} else {
						mtdChargedTons += (event.ScheduledChargedTons || 0) * ratio;
						mtdRolledTons += (event.ScheduledRolledTons || 0) * ratio;
						mtdFinishedTons += (event.ScheduledFinishedTons || 0) * ratio;
						mtdRollingHours += (event.ScheduledRollingHours || 0) * ratio;
					}
				}

				if (!countedEventIdsMonth.has(event.EventId)) {
					countedEventIdsMonth.add(event.EventId);

					monthSchedCharged += (event.ScheduledChargedTons || 0) * ratio;
					monthSchedRolled += (event.ScheduledRolledTons || 0) * ratio;
					monthSchedFinished += (event.ScheduledFinishedTons || 0) * ratio;
					monthSchedHours += scheduledHours;
					if (effectiveTargetTPH != null && scheduledHours > 0) {
						monthTargetWeightedTPH += effectiveTargetTPH * scheduledHours;
						monthTargetWeightHours += scheduledHours;
					}

					if (useActual) {
						// Actuals are already month-specific from GetEvents
						monthActCharged += event.ActualChargedTons || 0;
						monthActRolled += event.ActualRolledTons || 0;
						monthActFinished += event.ActualFinishedTons || 0;
						monthActHours += event.ActualRollingHours || 0;
					}
				}
			});

			if (isThresholdDay) {
				thresholdInsertIndex = result.length;
			}
		} else {
			result.push({
				EventId: -day,
				Start: currentDate.toDate(),
				End: currentDate.toDate(),
				IsParent: false,
				IsFirstEventOfDay: true,
			} as ProcessedRow);

			if (isThresholdDay) {
				thresholdInsertIndex = result.length;
			}
		}
	}

	result.forEach((event, index) => {
		const previousEvent = result[index - 1];
		event.IsFirstEventOfDay =
			!previousEvent ||
			!moment
				.tz(event.Start, serverTimeZone)
				.isSame(moment.tz(previousEvent.Start, serverTimeZone), "day");
	});

	// Insert MTD summary after the InProgress event (if any), or after threshold day otherwise.
	const mtdBudgetTPH =
		mtdSchedRollingHours > 0 ? mtdSchedRolled / mtdSchedRollingHours : null;
	const mtdTargetTPH =
		mtdTargetWeightHours > 0
			? mtdTargetWeightedTPH / mtdTargetWeightHours
			: null;
	const mtdTPH = mtdRollingHours > 0 ? mtdActRolled / mtdRollingHours : null;
	const mtdMillYield =
		mtdChargedTons > 0 ? (mtdRolledTons / mtdChargedTons) * 100 : null;
	const mtdFinishedYield =
		mtdChargedTons > 0 ? (mtdFinishedTons / mtdChargedTons) * 100 : null;

	const mtdRow: ProcessedRow = {
		EventId: -9999,
		Start: thresholdDay.toDate(),
		End: thresholdDay.toDate(),
		IsMTDSummary: true,
		IsFirstRow: true,
		Description: `MTD THROUGH ${today.format("MM/DD/YY")}`,
		ScheduledChargedTons: Math.round(mtdSchedCharged) || undefined,
		ScheduledRolledTons: Math.round(mtdSchedRolled) || undefined,
		ScheduledFinishedTons: Math.round(mtdSchedFinished) || undefined,
		ActualChargedTons: Math.round(mtdActCharged) || undefined,
		ActualRolledTons: Math.round(mtdActRolled) || undefined,
		ActualFinishedTons: Math.round(mtdActFinished) || undefined,
		ActualRollingHours: mtdRollingHours
			? Math.round(mtdRollingHours)
			: undefined,
		ActualHours: mtdRollingHours ? Math.round(mtdRollingHours) : undefined,
		BudgetTPH: roundToSingleDecimal(mtdBudgetTPH),
		EffectiveTargetTPH: roundToSingleDecimal(mtdTargetTPH),
		ActualTPH: roundToSingleDecimal(mtdTPH),
		ActualMillYield: mtdMillYield
			? Math.round(mtdMillYield * 10) / 10
			: undefined,
		ActualFinishedYield: mtdFinishedYield
			? Math.round(mtdFinishedYield * 10) / 10
			: undefined,
	};

	let mtdInsertIndex = thresholdInsertIndex;
	if (inProgressEvent) {
		const searchFrom =
			thresholdInsertIndex !== -1
				? thresholdInsertIndex - 1
				: result.length - 1;
		for (let i = searchFrom; i >= 0; i--) {
			if (result[i].EventId === inProgressEvent.EventId) {
				mtdInsertIndex = i + 1;
				break;
			}
		}
	}

	if (mtdInsertIndex !== -1 && isCurrentMonth) {
		result.splice(mtdInsertIndex, 0, mtdRow);
	}

	// Add Month Summary row at the end. While the month is still open use the
	// plan (Scheduled); once the month is over use Actual.
	const monthEnded = today.isAfter(monthEndDate);
	const monthRolled = monthEnded ? monthActRolled : monthSchedRolled;
	const monthCharged = monthEnded ? monthActCharged : monthSchedCharged;
	const monthFinished = monthEnded ? monthActFinished : monthSchedFinished;
	const monthHrs = monthEnded ? monthActHours : monthSchedHours;

	const monthBudgetTPH =
		monthSchedHours > 0 ? monthSchedRolled / monthSchedHours : null;
	const monthTargetTPH =
		monthTargetWeightHours > 0
			? monthTargetWeightedTPH / monthTargetWeightHours
			: null;
	const monthActualTPH =
		monthActHours > 0 ? monthActRolled / monthActHours : null;
	const monthMillYield =
		monthCharged > 0 ? (monthRolled / monthCharged) * 100 : null;
	const monthFinishedYield =
		monthCharged > 0 ? (monthFinished / monthCharged) * 100 : null;

	const monthSummaryRow: ProcessedRow = {
		EventId: -99999,
		Start: monthEndDate.toDate(),
		End: monthEndDate.toDate(),
		IsMonthSummary: true,
		IsFirstRow: true,
		Description: `MONTH SUMMARY`,
		ScheduledChargedTons: Math.round(monthSchedCharged) || undefined,
		ScheduledRolledTons: Math.round(monthSchedRolled) || undefined,
		ScheduledFinishedTons: Math.round(monthSchedFinished) || undefined,
		ActualChargedTons: Math.round(monthActCharged) || undefined,
		ActualRolledTons: Math.round(monthActRolled) || undefined,
		ActualFinishedTons: Math.round(monthActFinished) || undefined,
		ActualRollingHours: monthHrs ? Math.round(monthHrs) : undefined,
		ActualHours: monthHrs ? Math.round(monthHrs) : undefined,
		BudgetTPH: roundToSingleDecimal(monthBudgetTPH),
		EffectiveTargetTPH: roundToSingleDecimal(monthTargetTPH),
		ActualTPH: roundToSingleDecimal(monthActualTPH),
		ActualMillYield: monthMillYield
			? Math.round(monthMillYield * 10) / 10
			: undefined,
		ActualFinishedYield: monthFinishedYield
			? Math.round(monthFinishedYield * 10) / 10
			: undefined,
	};

	result.push(monthSummaryRow);

	return result;
};

export const filterProcessedData = (
	processedData: ProcessedRow[],
	filterValue: string,
): ProcessedRow[] => {
	if (!filterValue) return processedData;

	const searchLower = filterValue.toLowerCase();
	return processedData.filter((row) => {
		return (
			row.SectionName?.toLowerCase().includes(searchLower) ||
			row.EventComments?.toLowerCase().includes(searchLower) ||
			row.EventTypeCode?.toLowerCase().includes(searchLower) ||
			row.StatusName?.toLowerCase().includes(searchLower) ||
			row.Description?.toLowerCase().includes(searchLower)
		);
	});
};

export const calculateRowSpans = (
	filteredData: ProcessedRow[],
	visibleColumns: ColumnDef[],
): { spans: Map<string, number>; skipRows: Set<string> } | null => {
	const spans = new Map<string, number>();
	const skipRows = new Set<string>();

	for (let i = 0; i < filteredData.length; i++) {
		const row = filteredData[i];
		const eventId = row.EventId;

		for (const col of visibleColumns) {
			if (!col.spanRows && !col.spanRowsByDay) continue;

			const cellKey = `${i}-${col.field}`;
			if (skipRows.has(cellKey)) continue;

			if (row.IsMTDSummary || row.IsMonthSummary) {
				spans.set(cellKey, 1);
				continue;
			}

			if (col.spanRows) {
				if (eventId < 0) {
					spans.set(cellKey, 1);
					continue;
				}
				// Count consecutive rows with same EventId
				let spanCount = 1;
				for (let j = i + 1; j < filteredData.length; j++) {
					if (filteredData[j].EventId === eventId) {
						spanCount++;
						skipRows.add(`${j}-${col.field}`);
					} else {
						break;
					}
				}
				spans.set(cellKey, spanCount);
			} else if (col.spanRowsByDay) {
				// Count consecutive rows belonging to the same day, stopping at
				// summary rows or at the next row marked as IsFirstEventOfDay.
				let spanCount = 1;
				for (let j = i + 1; j < filteredData.length; j++) {
					const next = filteredData[j];
					if (next.IsMTDSummary || next.IsMonthSummary) {
						spanCount++;
						skipRows.add(`${j}-${col.field}`);
						continue;
					}
					if (next.IsFirstEventOfDay) break;
					spanCount++;
					skipRows.add(`${j}-${col.field}`);
				}
				spans.set(cellKey, spanCount);
			}
		}
	}

	return { spans, skipRows };
};

export const calculateColumnWidths = (
	visibleColumns: ColumnDef[],
): { width: number | string; isFixed: boolean }[] => {
	const totalFlex = visibleColumns.reduce(
		(sum, col) => sum + (col.width ? 0 : (col.flex ?? 1)),
		0,
	);

	return visibleColumns.map((col) => {
		if (col.width) {
			return { width: col.width, isFixed: true };
		}
		const flexValue = col.flex ?? 1;
		const percentage = (flexValue / totalFlex) * 100;
		return { width: `${percentage}%`, isFixed: false };
	});
};
